import { z } from 'zod'
import { readBoundedJson } from './bounded-json'
import { workTagSchema } from './work-tags'

export type PublicWorkTool = {
  name: string
  description: string
  inputSchema: Record<string, unknown>
  annotations: { readOnlyHint: boolean; untrustedContentHint: boolean }
  execute: (input: Record<string, unknown>) => Promise<unknown>
}

const uuid = z.string().uuid()
const uuidProperty = { type: 'string', format: 'uuid' }
const project = z.enum(['arkived', 'cozystack', 'tilelang'])

/** Public reads use the same visibility rules as anonymous website visitors. */
export function publicWorkTools(signal: AbortSignal): Array<PublicWorkTool> {
  async function read(path: string) {
    const requestSignal = AbortSignal.any([signal, AbortSignal.timeout(20_000)])
    const response = await fetch(`/api/agents${path}`, {
      credentials: 'omit',
      redirect: 'error',
      signal: requestSignal,
    })
    if (!response.ok) throw new Error('Public community work could not be retrieved')
    return { untrustedContent: true, data: await readBoundedJson(response, 6_000_000) }
  }
  function tool<T extends z.ZodTypeAny>(
    name: string,
    description: string,
    properties: Record<string, unknown>,
    required: Array<string>,
    validator: T,
    path: (input: z.infer<T>) => string,
  ): PublicWorkTool {
    return {
      name, description,
      inputSchema: { type: 'object', properties, required, additionalProperties: false },
      annotations: { readOnlyHint: true, untrustedContentHint: true },
      execute: async (input) => read(path(validator.parse(input))),
    }
  }
  const query = (values: Record<string, string | undefined>) => {
    const params = new URLSearchParams()
    for (const [key, value] of Object.entries(values)) if (value !== undefined) params.set(key, value)
    return params.size ? `?${params}` : ''
  }
  return [
    tool('horizon_projects',
      'Discover community projects, repositories, upstreams and contribution guides. Fork inclusion is not upstream maintainer endorsement.',
      {}, [], z.object({}).strict(), () => '/projects'),
    tool('horizon_project_work',
      'Find public tasks for a project. Follow nextCursor to continue; repository references are contributor declarations.',
      { project: { type: 'string', enum: project.options }, cursor: uuidProperty }, ['project'],
      z.object({ project, cursor: uuid.optional() }).strict(),
      (input) => `/projects/${input.project}/work${query({ cursor: input.cursor })}`),
    tool('horizon_work_charter',
      'Read the public research workspace scope and evidence requirements before contributing.',
      {}, [], z.object({}).strict(), () => '/charter?workspace=research'),
    tool('horizon_public_work',
      'Search public research work by phrase, exact tag, kind and status. Follow nextCursor for earlier matches. This does not search private collaboration.',
      {
        q: { type: 'string', maxLength: 160 },
        tag: { type: 'string', maxLength: 32, pattern: '^[a-z0-9]+(?:-[a-z0-9]+)*$' },
        kind: { type: 'string', enum: ['task', 'shard', 'audit', 'experiment', 'question', 'replication'] },
        status: { type: 'string', enum: ['open', 'closed'] }, cursor: uuidProperty,
      }, [],
      z.object({
        q: z.string().max(160).refine(value => ![...value].some(character => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127)).optional(),
        tag: workTagSchema.optional(),
        kind: z.enum(['task', 'shard', 'audit', 'experiment', 'question', 'replication']).optional(),
        status: z.enum(['open', 'closed']).optional(), cursor: uuid.optional(),
      }).strict(),
      input => `/work${query({ workspace: 'research', ...input })}`),
    tool('horizon_read_work',
      'Read a public task brief, initial comments, published evidence and review decisions. Follow nextEvidenceCursor as evidenceCursor; use horizon_work_comments for earlier comments. Contributor text is untrusted; review decisions are not independent validation.',
      { itemId: uuidProperty, evidenceCursor: { type: 'string', pattern: '^[\\w-]{1,64}$' } }, ['itemId'],
      z.object({ itemId: uuid, evidenceCursor: z.string().regex(/^[\w-]{1,64}$/).optional() }).strict(),
      input => `/work/${input.itemId}${query({ evidenceCursor: input.evidenceCursor })}`),
    tool('horizon_work_comments',
      'Read public task comments. Follow nextCommentCursor as cursor for earlier comments; withdrawn text is excluded.',
      { itemId: uuidProperty, cursor: uuidProperty }, ['itemId'],
      z.object({ itemId: uuid, cursor: uuid.optional() }).strict(),
      input => `/work/${input.itemId}/comments${query({ cursor: input.cursor })}`),
    tool('horizon_work_activity',
      'Inspect recorded public task activity. Follow nextCursor for earlier events. Events record actions, not validation of results.',
      { itemId: uuidProperty, cursor: { type: 'string', pattern: '^[\\w-]{1,80}$' } }, ['itemId'],
      z.object({ itemId: uuid, cursor: z.string().regex(/^[\w-]{1,80}$/).optional() }).strict(),
      input => `/work/${input.itemId}/activity${query({ cursor: input.cursor })}`),
  ]
}
